import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import PageLayout from "../../layout/PageLayout";

import {
  getCoupon,
  updateCoupon,
  deleteCoupon,
} from "./CouponService";

import "./Coupons.css";

function CouponDetails() {

  const { id } =
    useParams();

  const navigate =
    useNavigate();

  const [loading, setLoading] =
    useState(true);

  const [saving, setSaving] =
    useState(false);

  const [coupon, setCoupon] =
    useState(null);

  /* ==========================================
      LOAD COUPON
  ========================================== */

  useEffect(() => {

    loadCoupon();

  }, []);

  async function loadCoupon() {

    try {

      setLoading(true);

      const data =
        await getCoupon(id);

      if (!data) {

        alert(
          "Coupon not found."
        );

        navigate(
          "/admin/coupons"
        );

        return;

      }

      setCoupon({

        ...data,

        couponType:
          data.couponType ??
          "public",

        rewardType:
          data.rewardType ??
          "contacts",

        rewardValue:
          data.rewardValue ?? 0,

        userLimit:
          data.userLimit ?? 1,

        cooldownDays:
          data.cooldownDays ?? 0,

        minimumAppVersion:
          data.minimumAppVersion ??
          "",

        onlyNewUsers:
          data.onlyNewUsers ??
          false,

        allowExistingUsers:
          data.allowExistingUsers ??
          true,

        allowReferralUsers:
          data.allowReferralUsers ??
          true,

        active:
          data.active ?? true,

        startDate:
          formatDateForInput(
            data.startDate
          ),

        expiryDate:
          formatDateForInput(
            data.expiryDate
          ),

      });

    } catch (error) {

      console.error(error);

      alert(
        "Unable to load coupon."
      );

    } finally {

      setLoading(false);

    }

  }

  /* ==========================================
      FORMAT DATE
  ========================================== */

  function formatDateForInput(
    value,
  ) {

    if (!value)
      return "";

    try {

      const date =
        value.toDate
          ? value.toDate()
          : new Date(value);

      return date
        .toISOString()
        .split("T")[0];

    } catch {

      return "";

    }

  }

  /* ==========================================
      HANDLE CHANGE
  ========================================== */

  function handleChange(e) {

    const {
      name,
      value,
      checked,
      type,
    } = e.target;

    setCoupon((prev) => ({

      ...prev,

      [name]:
        type === "checkbox"
          ? checked
          : value,

    }));

  }
    /* ==========================================
      SAVE
  ========================================== */

  async function handleSave() {

    if (!coupon)
      return;

    if (!coupon.code.trim()) {

      alert(
        "Coupon code is required."
      );

      return;

    }

    if (

      new Date(coupon.expiryDate)

      <=

      new Date(coupon.startDate)

    ) {

      alert(
        "Expiry date must be after start date."
      );

      return;

    }

    try {

      setSaving(true);

      const maxUses =
        Number(
          coupon.maxUses
        );

      const rewardValue =
        Number(
          coupon.rewardValue
        );

      const usedCount =
        Number(
          coupon.usedCount || 0
        );

      await updateCoupon(

        id,

        {

          title:
            coupon.title.trim(),

          description:
            coupon.description.trim(),

          couponType:
            coupon.couponType,

          rewardType:
            coupon.rewardType,

          rewardValue,

          rewardTitle:
            `${rewardValue} Contacts`,

          image:
            coupon.image || "",

          maxUses,

          usedCount,

          remainingUses:
            Math.max(
              maxUses - usedCount,
              0,
            ),

          userLimit:
            Number(
              coupon.userLimit
            ),

          cooldownDays:
            Number(
              coupon.cooldownDays
            ),

          minimumAppVersion:
            coupon.minimumAppVersion,

          onlyNewUsers:
            coupon.onlyNewUsers,

          allowExistingUsers:
            coupon.allowExistingUsers,

          allowReferralUsers:
            coupon.allowReferralUsers,

          totalRewardGiven:
            Number(
              coupon.totalRewardGiven || 0
            ),

          active:
            coupon.active,

          status:
            coupon.active
              ? "active"
              : "inactive",

          startDate:
            new Date(
              coupon.startDate
            ),

          expiryDate:
            new Date(
              coupon.expiryDate
            ),

        }

      );

      alert(
        "Coupon updated successfully."
      );

      await loadCoupon();

    } catch (error) {

      console.error(error);

      alert(
        "Unable to update coupon."
      );

    } finally {

      setSaving(false);

    }

  }

  /* ==========================================
      DELETE
  ========================================== */

  async function handleDelete() {

    const confirmed =
      window.confirm(
        `Delete coupon "${coupon.code}" ?`
      );

    if (!confirmed)
      return;

    try {

      await deleteCoupon(id);

      alert(
        "Coupon deleted successfully."
      );

      navigate(
        "/admin/coupons"
      );

    } catch (error) {

      console.error(error);

      alert(
        "Unable to delete coupon."
      );

    }

  }
  /* ==========================================
    LOADING
========================================== */

if (loading) {

  return (

    <PageLayout
      title="Coupon Details"
    >

      <div
        style={{
          padding: 80,
          textAlign: "center",
        }}
      >

        <h2>
          Loading Coupon...
        </h2>

      </div>

    </PageLayout>

  );

}

if (!coupon) {

  return (

    <PageLayout
      title="Coupon Details"
    >

      <div
        style={{
          padding: 80,
          textAlign: "center",
        }}
      >

        <h2>
          Coupon Not Found
        </h2>

      </div>

    </PageLayout>

  );

}

/* ==========================================
    UI
========================================== */

return (

<PageLayout

title="Coupon Details"

subtitle="View & manage coupon."

>

<form
className="coupon-form"
>

{/* ==========================================
    COUPON INFORMATION
========================================== */}

<div className="coupon-section">

<h3>

🎟 Coupon Information

</h3>

<div className="coupon-grid">

<div className="coupon-field">

<label>

Coupon Code

</label>

<input

value={coupon.code}

disabled

/>

</div>

<div className="coupon-field">

<label>

Coupon Name

</label>

<input

name="title"

value={coupon.title}

onChange={handleChange}

/>

</div>

<div className="coupon-field full-width">

<label>

Description

</label>

<textarea

rows={4}

name="description"

value={coupon.description}

onChange={handleChange}

/>

</div>

<div className="coupon-field">

<label>

Coupon Type

</label>

<select

name="couponType"

value={coupon.couponType}

onChange={handleChange}

>

<option value="public">

Public

</option>

<option value="private">

Private

</option>

<option value="referral">

Referral

</option>

</select>

</div>

</div>

</div>
{/* ==========================================
    REWARD
========================================== */}

<div className="coupon-section">

  <h3>
    🎁 Reward
  </h3>

  <div className="coupon-grid">

    <div className="coupon-field">

      <label>
        Reward Type
      </label>

      <select
        name="rewardType"
        value={coupon.rewardType}
        onChange={handleChange}
      >

        <option value="contacts">
          Contacts
        </option>

      </select>

    </div>

    <div className="coupon-field">

      <label>
        Reward Value
      </label>

      <input
        type="number"
        min="1"
        name="rewardValue"
        value={coupon.rewardValue}
        onChange={handleChange}
      />

    </div>

  </div>

</div>

{/* ==========================================
    USAGE LIMITS
========================================== */}

<div className="coupon-section">

  <h3>
    📊 Usage Limits
  </h3>

  <div className="coupon-grid">

    <div className="coupon-field">

      <label>
        Maximum Uses
      </label>

      <input
        type="number"
        min="1"
        name="maxUses"
        value={coupon.maxUses}
        onChange={handleChange}
      />

    </div>

    <div className="coupon-field">

      <label>
        Used Count
      </label>

      <input
        value={coupon.usedCount || 0}
        disabled
      />

    </div>

    <div className="coupon-field">

      <label>
        Remaining Uses
      </label>

      <input
        value={coupon.remainingUses || 0}
        disabled
      />

    </div>

    <div className="coupon-field">

      <label>
        Per User Usage Limit
      </label>

      <input
        type="number"
        min="1"
        name="userLimit"
        value={coupon.userLimit}
        onChange={handleChange}
      />

    </div>

    <div className="coupon-field">

      <label>
        Cooldown Days
      </label>

      <input
        type="number"
        min="0"
        name="cooldownDays"
        value={coupon.cooldownDays}
        onChange={handleChange}
      />

      <small
        style={{
          color: "#6B7280",
          marginTop: 6,
        }}
      >
        0 = No waiting period before the user can redeem again (subject to the per-user limit).
      </small>

    </div>

  </div>

</div>
{/* ==========================================
    VALIDITY
========================================== */}

<div className="coupon-section">

  <h3>
    📅 Coupon Validity
  </h3>

  <div className="coupon-grid">

    <div className="coupon-field">

      <label>
        Start Date
      </label>

      <input
        type="date"
        name="startDate"
        value={coupon.startDate}
        onChange={handleChange}
      />

    </div>

    <div className="coupon-field">

      <label>
        Expiry Date
      </label>

      <input
        type="date"
        name="expiryDate"
        value={coupon.expiryDate}
        onChange={handleChange}
      />

    </div>

  </div>

</div>

{/* ==========================================
    USER RESTRICTIONS
========================================== */}

<div className="coupon-section">

  <h3>
    👤 User Restrictions
  </h3>

  <div className="coupon-grid">

    <div className="coupon-field">

      <label>
        Minimum App Version
      </label>

      <input
        name="minimumAppVersion"
        placeholder="1.0.0"
        value={coupon.minimumAppVersion}
        onChange={handleChange}
      />

    </div>

    <div className="coupon-field">

      <label>
        Status
      </label>

      <select
        name="active"
        value={coupon.active ? "true" : "false"}
        onChange={(e) =>
          setCoupon((prev) => ({
            ...prev,
            active: e.target.value === "true",
          }))
        }
      >

        <option value="true">
          Active
        </option>

        <option value="false">
          Inactive
        </option>

      </select>

    </div>

  </div>

  <div
    style={{
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit,minmax(240px,1fr))",
      gap: 18,
      marginTop: 24,
    }}
  >

    <label className="coupon-checkbox">

      <input
        type="checkbox"
        name="onlyNewUsers"
        checked={coupon.onlyNewUsers}
        onChange={handleChange}
      />

      Only New Users

    </label>

    <label className="coupon-checkbox">

      <input
        type="checkbox"
        name="allowExistingUsers"
        checked={coupon.allowExistingUsers}
        onChange={handleChange}
      />

      Existing Users Allowed

    </label>

    <label className="coupon-checkbox">

      <input
        type="checkbox"
        name="allowReferralUsers"
        checked={coupon.allowReferralUsers}
        onChange={handleChange}
      />

      Referral Users Allowed

    </label>

  </div>

</div>
{/* ==========================================
    COUPON STATISTICS
========================================== */}

<div className="coupon-section">

  <h3>
    📈 Coupon Statistics
  </h3>

  <div className="coupon-stats">

    <div className="coupon-stat">

      <p>Total Uses</p>

      <h2>
        {coupon.usedCount || 0}
      </h2>

    </div>

    <div className="coupon-stat">

      <p>Remaining Uses</p>

      <h2>
        {coupon.remainingUses || 0}
      </h2>

    </div>

    <div className="coupon-stat">

      <p>Reward Distributed</p>

      <h2>
        {coupon.totalRewardGiven || 0}
      </h2>

    </div>

    <div className="coupon-stat">

      <p>Status</p>

      <h2
        style={{
          color: coupon.active
            ? "#16A34A"
            : "#DC2626",
        }}
      >
        {
          coupon.active
            ? "Active"
            : "Inactive"
        }
      </h2>

    </div>

  </div>

</div>

{/* ==========================================
    ACTIONS
========================================== */}

<div className="coupon-actions">

  <button
    type="button"
    className="coupon-btn coupon-btn-secondary"
    onClick={() =>
      navigate("/admin/coupons")
    }
  >
    ← Back
  </button>

  <button
    type="button"
    className="coupon-btn coupon-btn-danger"
    onClick={handleDelete}
  >
    🗑 Delete Coupon
  </button>

  <button
    type="button"
    className="coupon-btn coupon-btn-primary"
    disabled={saving}
    onClick={handleSave}
  >
    {
      saving
        ? "Saving..."
        : "💾 Save Changes"
    }
  </button>

</div>

</form>

</PageLayout>

);

}

export default CouponDetails;